import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import SkeletonCell from '../utils/SkeletonCell';
import SearchNoResults from '../Search/components/SearchNoResults';
import { TableColumn as BaseTableColumn, SearchNoResultsVariant } from '../../types/search';

interface TableColumnWithWidth extends BaseTableColumn {
  width?: string;
  skeletonCount?: number;
}

interface IBaseListingProps {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  results: Record<string, unknown>[];
  columns: TableColumnWithWidth[];
  filters?: React.ReactNode;
  pagination?: React.ReactNode;
  resultsContainer?: React.ReactNode;
  listingType?: string;
  clearAllFilters?: () => void;
  updateSearchTerm?: (searchTerm: string) => void;
  noResultsVariant?: SearchNoResultsVariant;
}

const BaseListing: React.FC<IBaseListingProps> = ({
  isLoading,
  isError,
  isSuccess,
  results,
  columns,
  filters,
  pagination,
  resultsContainer,
  listingType,
  clearAllFilters,
  updateSearchTerm,
  noResultsVariant = 'search',
}) => {
  const skeletonRows = 10;
  const [showSkeleton, setShowSkeleton] = useState(false);

  const renderContent = () => {
    if (showSkeleton) {
      return null; // Will be handled in main render
    }
    if (results && results.length > 0 && resultsContainer) {
      return <>{resultsContainer}</>;
    }
    return (
      <div className="listing__no-results">
        {isError && (
          <SearchNoResults
            clearAllFilters={clearAllFilters}
            updateSearchTerm={updateSearchTerm}
            variant={noResultsVariant}
          />
        )}
        {isSuccess && results.length === 0 && (
          <SearchNoResults
            clearAllFilters={clearAllFilters}
            updateSearchTerm={updateSearchTerm}
            variant={noResultsVariant}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isLoading) {
      setShowSkeleton(true);
    } else if (showSkeleton) {
      timer = setTimeout(() => {
        setShowSkeleton(false);
      }, 1000); // Mimic a 1s delay in loading for smoother UX
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, showSkeleton]);

  return (
    <div className="listing__container">
      {filters}
      {showSkeleton ? (
        <div className="listing__skeletons">
          <table
            className={clsx(
              'listing__skeleton-table',
              listingType && ['dynamic-table', `dynamic-table--${listingType}`]
            )}
          >
            <thead>
              <tr className={clsx(listingType && ['dynamic-table__header', `dynamic-table__header--${listingType}`])}>
                {columns.map(col => {
                  const getExtensionClass = () => {
                    if (!listingType) return '';
                    if (col.key === 'checkbox') return ' dynamic-table__header-cell--all-viewports';
                    if (col.key === 'mobileOnly') return ' dynamic-table__header-cell--mobile-only';
                    return '';
                  };
                  const extensionClass = getExtensionClass();
                  return (
                    <th
                      key={col.key}
                      style={{ width: col.width ?? '20%' }}
                      className={clsx(
                        listingType && ['dynamic-table__header-cell', `dynamic-table__header-cell--${listingType}`],
                        extensionClass
                      )}
                    >
                      {col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                <tr
                  key={`skeleton-row-${rowIdx}`}
                  className={clsx(listingType && ['dynamic-table__row', `dynamic-table__row--${listingType}`])}
                >
                  {columns.map(col => {
                    return (
                      <td
                        key={col.key}
                        style={{ width: col.width ?? '20%' }}
                        className={clsx(
                          listingType && ['dynamic-table__cell', `dynamic-table__cell--${listingType}`],
                          listingType && col.key === 'mobileOnly' && 'dynamic-table__cell--mobile-only'
                        )}
                      >
                        {Array.from({ length: col.skeletonCount ?? 1 }).map((_, itemIdx) => (
                          <SkeletonCell
                            key={`${col.key}-${itemIdx}`}
                            width={col.skeletonWidths ? col.skeletonWidths[itemIdx] : (col.width ?? '20%')}
                          />
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        renderContent()
      )}
      {pagination}
    </div>
  );
};

export default BaseListing;
